import { authDebug } from '@/composables/useAuthDebug'
import { ApiResponse, AuthResponse, TelegramAuthRequest, User } from '@/types'
import { telegramWebApp } from '@/utils/telegram'
import { apiService } from './api'

export class AuthService {
    private static instance: AuthService
    private currentUser: User | null = null
    private token: string | null = null
    private refreshToken: string | null = null
    private refreshPromise: Promise<boolean> | null = null

    // Добавляем защиту от бесконечных циклов и параллельных запросов
    private isReauthenticating = false
    private reauthPromise: Promise<boolean> | null = null
    private onReauthSuccess?: () => void // Callback для уведомления store

    private constructor() {
        this.loadFromStorage()
        // Subscribe to global 401 from ApiService
        apiService.setOnUnauthorized(async () => {
            await this.processUnauthorized()
        })
        // Регистрируем callback для refresh токена
        apiService.setOnRefreshToken(() => {
            return this.refreshAuthToken()
        })
    }

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService()
        }
        return AuthService.instance
    }

    private loadFromStorage(): void {
        this.token = localStorage.getItem('access_token')
        this.refreshToken = localStorage.getItem('refresh_token')
        const userStr = localStorage.getItem('user')

        console.log('📦 Loading from storage:', {
            hasToken: !!this.token,
            hasRefreshToken: !!this.refreshToken,
            hasUser: !!userStr,
        })

        authDebug.addEvent('load', 'Loading from localStorage', {
            hasToken: !!this.token,
            hasRefreshToken: !!this.refreshToken,
            hasUser: !!userStr,
        })

        if (userStr) {
            try {
                this.currentUser = JSON.parse(userStr)
                authDebug.updateCurrentUser(this.currentUser)
                authDebug.addEvent('load', 'User loaded from storage', {
                    userId: this.currentUser?.id,
                    telegramId: this.currentUser?.telegram_id,
                })
            } catch (error) {
                console.error('Failed to parse user from storage:', error)
                authDebug.addError(`Failed to parse user from storage: ${error}`)
                this.clearAuth()
            }
        }

        authDebug.updateTokens(this.token, this.refreshToken)
        authDebug.updateLocalStorageInfo()
    }

    private saveToStorage(): void {
        console.log('💾 Saving to localStorage:', {
            token: !!this.token,
            refreshToken: !!this.refreshToken,
            user: !!this.currentUser,
        })

        authDebug.addEvent('save', 'Saving to localStorage', {
            token: !!this.token,
            refreshToken: !!this.refreshToken,
            user: !!this.currentUser,
            userId: this.currentUser?.id,
            telegramId: this.currentUser?.telegram_id,
        })

        if (this.token) {
            localStorage.setItem('access_token', this.token)
            console.log('✅ Saved access_token to localStorage')
        }
        if (this.refreshToken) {
            localStorage.setItem('refresh_token', this.refreshToken)
            console.log('✅ Saved refresh_token to localStorage')
        }
        if (this.currentUser) {
            localStorage.setItem('user', JSON.stringify(this.currentUser))
            console.log('✅ Saved user to localStorage')
        }

        // Проверим, что реально сохранилось
        const savedToken = localStorage.getItem('access_token')
        const savedUser = localStorage.getItem('user')
        console.log('🔍 Verification - localStorage contains:', {
            hasToken: !!savedToken,
            hasUser: !!savedUser,
        })

        authDebug.updateTokens(this.token, this.refreshToken)
        authDebug.updateCurrentUser(this.currentUser)
        authDebug.updateLocalStorageInfo()
    }

    private clearAuth(): void {
        authDebug.addEvent('clear', 'Clearing auth data', {
            hadUser: !!this.currentUser,
            userId: this.currentUser?.id,
            telegramId: this.currentUser?.telegram_id,
            hadToken: !!this.token,
            hadRefreshToken: !!this.refreshToken,
        })

        this.currentUser = null
        this.token = null
        this.refreshToken = null
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')

        authDebug.updateCurrentUser(null)
        authDebug.updateTokens(null, null)
        authDebug.updateLocalStorageInfo()
    }

    private handleUnauthorized<T>(
        response: ApiResponse<T> | { success: false; error?: { code?: number } }
    ): boolean {
        if (response && !response.success && response.error?.code === 401) {
            this.clearAuth()
            return true
        }
        return false
    }

    public async loginWithTelegram(): Promise<{ success: boolean; error?: string }> {
        try {
            authDebug.addEvent('login', 'Starting Telegram login')
            authDebug.updateTelegramUser()

            // Получаем данные пользователя от Telegram WebApp
            const user = telegramWebApp.getUser()
            if (!user) {
                authDebug.addError('No Telegram user data available')
                return { success: false, error: 'No Telegram user data available' }
            }

            // Проверяем, не изменился ли пользователь Telegram
            if (this.currentUser && this.currentUser.telegram_id !== user.id) {
                console.log('⚠️ Telegram user changed, clearing old auth data', {
                    oldTelegramId: this.currentUser.telegram_id,
                    newTelegramId: user.id,
                })
                authDebug.addEvent('login', 'Telegram user changed, clearing old auth', {
                    oldTelegramId: this.currentUser.telegram_id,
                    newTelegramId: user.id,
                })
                this.clearAuth()
            }

            // Формируем запрос в формате, который ожидает бэкенд
            const telegramUser: TelegramAuthRequest = {
                telegram_id: user.id,
                username: user.username || null,
                first_name: user.first_name,
                last_name: user.last_name || null,
                language_code: user.language_code || null,
                is_premium: user.is_premium || false,
                photo_url: user.photo_url || null,
                email: null, // Email не доступен из Telegram WebApp
                init_data: telegramWebApp.getInitData(),
            }

            authDebug.addEvent('login', 'Sending Telegram auth request', {
                telegramId: user.id,
                username: user.username,
            })

            const response = await apiService.authTelegramUser(telegramUser)

            if (response.success && response.data) {
                const authData = response.data as AuthResponse
                this.token = authData.tokens.access
                this.refreshToken = authData.tokens.refresh
                // Добавляем telegram_id из Telegram WebApp, так как бэкенд его не возвращает
                this.currentUser = {
                    ...authData.user,
                    telegram_id: user.id,
                }
                this.saveToStorage()

                authDebug.addEvent('login', 'Telegram login successful', {
                    userId: this.currentUser.id,
                    telegramId: this.currentUser.telegram_id,
                })
                authDebug.refreshAll()

                return { success: true }
            } else {
                authDebug.addError(
                    `Telegram login failed: ${response.error?.message || 'Unknown error'}`
                )
                this.handleUnauthorized(response)
                return {
                    success: false,
                    error: response.error?.message || 'Authentication failed',
                }
            }
        } catch (error: unknown) {
            console.error('Login error:', error)
            authDebug.addError(`Login error: ${(error as Error).message || 'Unknown error'}`)
            return {
                success: false,
                error: (error as Error).message || 'Login failed',
            }
        }
    }

    public async loginWithCredentials(
        username: string,
        password: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            console.log('🔑 Attempting login with credentials:', { username })
            const response = await apiService.authLogin(username, password)
            console.log('🔑 Login response:', response)

            if (response.success && response.data) {
                const authData = response.data as AuthResponse
                console.log('🔑 Auth data received:', {
                    user: authData.user,
                    hasTokens: !!(authData.tokens?.access && authData.tokens?.refresh),
                })

                this.token = authData.tokens.access
                this.refreshToken = authData.tokens.refresh
                this.currentUser = authData.user
                this.saveToStorage()

                console.log('💾 Saved to storage:', {
                    hasToken: !!this.token,
                    hasUser: !!this.currentUser,
                })
                return { success: true }
            } else {
                console.log('❌ Login failed:', response.error)
                this.handleUnauthorized(response)
                return {
                    success: false,
                    error: response.error?.message || 'Authentication failed',
                }
            }
        } catch (error: unknown) {
            console.error('Login error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Login failed',
            }
        }
    }

    public async loginWithTelegramUser(
        telegramUser: TelegramAuthRequest
    ): Promise<{ success: boolean; error?: string }> {
        try {
            console.log('🤖 Logging in with Telegram user data:', telegramUser)

            // Гарантируем, что init_data присутствует в запросе
            const payload: TelegramAuthRequest = {
                ...telegramUser,
                init_data: telegramUser.init_data ?? telegramWebApp.getInitData(),
            }
            const response = await apiService.authTelegramUser(payload)

            if (response.success && response.data) {
                const authData = response.data as AuthResponse
                this.token = authData.tokens.access
                this.refreshToken = authData.tokens.refresh
                // Добавляем telegram_id из Telegram WebApp, так как бэкенд его не возвращает
                const telegramWebAppUser = telegramWebApp.getUser()
                this.currentUser = {
                    ...authData.user,
                    telegram_id: telegramWebAppUser?.id ?? telegramUser.telegram_id ?? undefined,
                }
                this.saveToStorage()
                console.log('✅ Telegram user login successful:', authData.user)
                return { success: true }
            } else {
                console.error('❌ Telegram user login failed:', response.error)
                this.handleUnauthorized(response)
                return {
                    success: false,
                    error: response.error?.message || 'Telegram authentication failed',
                }
            }
        } catch (error: unknown) {
            console.error('❌ Telegram user login error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Telegram login failed',
            }
        }
    }

    public async logout(): Promise<{ success: boolean; error?: string }> {
        try {
            authDebug.addEvent('logout', 'Starting logout')
            if (this.token) {
                // Пытаемся отправить запрос на logout, но не критично, если он не удастся
                try {
                    await apiService.logout()
                    authDebug.addEvent('logout', 'Logout request sent successfully')
                } catch (error) {
                    // Игнорируем ошибки logout - все равно очищаем локальные данные
                    console.warn('Logout request failed, clearing local auth anyway:', error)
                    authDebug.addError(
                        `Logout request failed: ${(error as Error).message || 'Unknown error'}`
                    )
                }
            }
            this.clearAuth()
            authDebug.addEvent('logout', 'Logout completed')
            return { success: true }
        } catch (error: unknown) {
            console.error('Logout error:', error)
            authDebug.addError(`Logout error: ${(error as Error).message || 'Unknown error'}`)
            // Clear auth even if logout request fails
            this.clearAuth()
            return { success: true }
        }
    }

    public isAuthenticated(): boolean {
        return !!this.token
    }

    public getCurrentUser(): User | null {
        return this.currentUser
    }

    public getToken(): string | null {
        return this.token
    }

    public getRefreshToken(): string | null {
        return this.refreshToken
    }

    /**
     * Проверяет, соответствует ли сохраненный пользователь текущему пользователю Telegram
     */
    public checkTelegramUserMatch(): boolean {
        const currentUser = this.currentUser
        const telegramUser = telegramWebApp.getUser()

        authDebug.updateTelegramUser()
        authDebug.updateCurrentUser(currentUser)

        if (!telegramUser) {
            // Если нет данных от Telegram - считаем что не совпадает
            authDebug.addEvent('check', 'User match check: No Telegram user data', { match: false })
            authDebug.updateUserMatch(false)
            return false
        }

        if (!currentUser) {
            // Если нет сохраненного пользователя - считаем что не совпадает
            authDebug.addEvent('check', 'User match check: No saved user', { match: false })
            authDebug.updateUserMatch(false)
            return false
        }

        // Если у сохраненного пользователя нет telegram_id,
        // это может быть пользователь, авторизованный через обычный логин
        // В этом случае считаем, что пользователь не совпадает с Telegram
        if (!currentUser.telegram_id) {
            authDebug.addEvent('check', 'User match check: Saved user has no telegram_id', {
                match: false,
            })
            authDebug.updateUserMatch(false)
            return false
        }

        // Сравниваем telegram_id
        const match = currentUser.telegram_id === telegramUser.id
        authDebug.addEvent('check', 'User match check', {
            match,
            savedTelegramId: currentUser.telegram_id,
            currentTelegramId: telegramUser.id,
        })
        authDebug.updateUserMatch(match)
        return match
    }

    public async refreshAuthToken(): Promise<boolean> {
        if (!this.refreshToken) {
            authDebug.addEvent('refresh', 'Token refresh: No refresh token available', {
                success: false,
            })
            return false
        }
        if (this.refreshPromise) return this.refreshPromise
        this.refreshPromise = (async () => {
            try {
                authDebug.addEvent('refresh', 'Attempting token refresh')
                const response = await apiService.refreshToken(this.refreshToken!)

                if (response.success && response.data) {
                    const data = response.data as { access?: string; refresh?: string }

                    if (data.access && data.refresh) {
                        this.token = data.access
                        this.refreshToken = data.refresh
                        this.saveToStorage()
                        authDebug.addEvent('refresh', 'Token refresh successful')
                        return true
                    } else {
                        console.error('Unexpected refresh token response structure:', data)
                        authDebug.addError('Unexpected refresh token response structure')
                        this.clearAuth()
                        return false
                    }
                } else {
                    authDebug.addError(
                        `Token refresh failed: ${response.error?.message || 'Unknown error'}`
                    )
                    this.clearAuth()
                    return false
                }
            } catch (error) {
                console.error('Token refresh error:', error)
                authDebug.addError(
                    `Token refresh error: ${(error as Error).message || 'Unknown error'}`
                )
                this.clearAuth()
                return false
            } finally {
                this.refreshPromise = null
            }
        })()
        return this.refreshPromise
    }

    public async updateUser(
        userData: Partial<User>
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await apiService.updateMe(userData)

            if (response.success && response.data) {
                this.currentUser = { ...this.currentUser, ...response.data }
                this.saveToStorage()
                return { success: true }
            } else {
                this.handleUnauthorized(response)
                return {
                    success: false,
                    error: response.error?.message || 'Update failed',
                }
            }
        } catch (error: unknown) {
            console.error('Update user error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Update failed',
            }
        }
    }

    public async getUserStats(): Promise<{ success: boolean; data?: unknown; error?: string }> {
        try {
            const response = await apiService.getMyStats()

            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                this.handleUnauthorized(response)
                return {
                    success: false,
                    error: response.error?.message || 'Failed to get user stats',
                }
            }
        } catch (error: unknown) {
            console.error('Get user stats error:', error)
            return {
                success: false,
                error: (error as Error).message || 'Failed to get user stats',
            }
        }
    }

    public hasRole(role: string): boolean {
        return this.currentUser?.role === role
    }

    public hasPermission(permission: string): boolean {
        if (!this.currentUser?.permissions) return false
        return this.currentUser.permissions.includes(permission)
    }

    public isAdmin(): boolean {
        return this.hasRole('admin')
    }

    public isModerator(): boolean {
        return this.hasRole('moderator') || this.isAdmin()
    }

    /**
     * Устанавливает callback, который будет вызван после успешной реавторизации
     * Используется для синхронизации с Pinia store
     */
    public setOnReauthSuccess(callback: () => void): void {
        this.onReauthSuccess = callback
    }

    /**
     * Обрабатывает ситуацию 401 от apiService: очищает авторизацию и пытается автоматически залогиниться
     */
    private async processUnauthorized(): Promise<void> {
        // Предотвращаем бесконечные циклы
        if (this.isReauthenticating) {
            console.log('⚠️ Reauthentication already in progress, skipping...')
            return
        }

        console.log('🔐 Handling unauthorized (401), clearing auth...')
        this.clearAuth()

        // Пытаемся автоматически залогиниться через Telegram
        const reauthSuccess = await this.attemptReauth()

        if (reauthSuccess) {
            console.log('✅ Reauthentication successful')
            // Уведомляем store о успешной реавторизации
            this.onReauthSuccess?.()
        } else {
            console.log('❌ Reauthentication failed - user will need to login manually')
        }
    }

    /**
     * Пытается автоматически залогиниться через Telegram
     * Защищена от параллельных вызовов - все запросы будут ждать один промис
     */
    private async attemptReauth(): Promise<boolean> {
        // Если уже идет процесс реавторизации, возвращаем существующий промис
        if (this.reauthPromise) {
            return this.reauthPromise
        }

        // Создаем новый промис для реавторизации
        this.reauthPromise = (async () => {
            this.isReauthenticating = true

            try {
                // Проверяем, есть ли данные от Telegram
                const telegramUser = telegramWebApp.getUser()
                if (!telegramUser) {
                    console.log('⚠️ No Telegram user data available for reauth')
                    return false
                }

                console.log('🔄 Attempting automatic Telegram reauthentication...')
                const result = await this.loginWithTelegram()

                return result.success
            } catch (error) {
                console.error('❌ Reauthentication error:', error)
                return false
            } finally {
                this.isReauthenticating = false
                // Очищаем промис после завершения
                this.reauthPromise = null
            }
        })()

        return this.reauthPromise
    }
}

export const authService = AuthService.getInstance()
